import type { Card, CardsFilterOptions, SetOption } from '@/types'
import { getSetPriority } from './sortCards'

interface MutableRange {
  min: number
  max: number
}

const newRange = (): MutableRange => ({ min: Infinity, max: -Infinity })

function trackRange(range: MutableRange, value: number | null | undefined): void {
  if (value == null) return
  range.min = Math.min(range.min, value)
  range.max = Math.max(range.max, value)
}

/** An empty range (no values seen) falls back to the given defaults. */
function finishRange(range: MutableRange, defaultMin: number, defaultMax: number): { min: number; max: number } {
  return {
    min: range.min === Infinity ? defaultMin : range.min,
    max: range.max === -Infinity ? defaultMax : range.max,
  }
}

/** Add a `;`-joined multi-value field: "Red;Blue" → "Red" and "Blue". */
function addSplit(into: Set<string>, value: string | null | undefined): void {
  if (!value) return
  for (const part of value.split(';')) into.add(part.trim())
}

function addIfPresent(into: Set<string>, value: string | null | undefined): void {
  if (value) into.add(value)
}

interface Accumulator {
  colors: Set<string>
  cardTypes: Set<string>
  rarities: Set<string>
  variantTypes: Set<string>
  attributes: Set<string>
  subTypes: Set<string>
  counters: Set<number>
  setsMap: Map<string, SetOption>
  costRange: MutableRange
  powerRange: MutableRange
  priceRange: MutableRange
}

function newAccumulator(): Accumulator {
  return {
    colors: new Set(),
    cardTypes: new Set(),
    rarities: new Set(),
    variantTypes: new Set(),
    attributes: new Set(),
    subTypes: new Set(),
    counters: new Set(),
    setsMap: new Map(),
    costRange: newRange(),
    powerRange: newRange(),
    priceRange: newRange(),
  }
}

function collectCard(acc: Accumulator, card: Card): void {
  addSplit(acc.colors, card.color)
  addSplit(acc.attributes, card.attribute)
  addIfPresent(acc.cardTypes, card.cardType)
  addIfPresent(acc.rarities, card.rarity)
  addIfPresent(acc.variantTypes, card.variantType)
  for (const st of card.subTypes ?? []) acc.subTypes.add(st)
  if (card.counter != null) acc.counters.add(card.counter)
  if (card.setName && card.setCode && !acc.setsMap.has(card.setName)) {
    acc.setsMap.set(card.setName, { setName: card.setName, setCode: card.setCode })
  }
  trackRange(acc.costRange, card.cost)
  trackRange(acc.powerRange, card.power)
  trackRange(acc.priceRange, card.marketPrice)
}

function compareSets(a: SetOption, b: SetOption): number {
  return getSetPriority(a.setCode) - getSetPriority(b.setCode) || a.setCode.localeCompare(b.setCode)
}

export function deriveCardFilterOptions(cards: Card[]): CardsFilterOptions {
  const acc = newAccumulator()
  for (const card of cards) collectCard(acc, card)

  return {
    colors: [...acc.colors].sort(),
    cardTypes: [...acc.cardTypes].sort(),
    rarities: [...acc.rarities].sort(),
    variantTypes: [...acc.variantTypes].sort(),
    attributes: [...acc.attributes].sort(),
    subTypes: [...acc.subTypes].sort(),
    counters: [...acc.counters].sort((a, b) => a - b),
    sets: [...acc.setsMap.values()].sort(compareSets),
    costRange: finishRange(acc.costRange, 0, 10),
    powerRange: finishRange(acc.powerRange, 0, 12000),
    priceRange: finishRange(acc.priceRange, 0, 100),
  }
}
